import { AccountModel } from '@ajs/models/account';
import AppStateService from '@ajs/services/AppStateService';
import { inject, Injectable } from '@angular/core';
import { AccountFeatureFlag } from '@app/core/models/enums/account-feature-flag.enum';
import { GOOGLE_UCP_GATED_LABEL } from '@app/exports/models/constants/export-destinations.constants';
import { ExportCatalogDestinationInterface } from '@app/exports/models/interfaces/export-destination-catalog.interface';
import { ExportDestination } from '@app/exports/models/types/export-destination.type';

/**
 * FP-18125: hides destinations from the destination pickers unless the account holds the paired
 * feature.
 *
 * This whole file is temporary. Entries in `GATED_DESTINATION_LABELS` live here only while a
 * destination is in closed beta; the map is expected to be empty (and the file deleted along with
 * its call sites) once every gated destination is generally available.
 */
@Injectable({
    providedIn: 'root'
})
export class GatedExportDestinationsService {

    /**
     * Gated destinations keyed by display label, which is both what the legacy constants and the
     * `destinations.json` catalog surface and what is persisted in `exports.destination` — so one
     * entry covers both option sources.
     */
    private static readonly GATED_DESTINATION_LABELS: ReadonlyMap<string, AccountFeatureFlag> = new Map<string, AccountFeatureFlag>([
        [GOOGLE_UCP_GATED_LABEL, AccountFeatureFlag.GOOGLE_UCP_BETA]
    ]);

    private readonly appStateService: AppStateService = inject(AppStateService);

    /** Drop gated options from a legacy (hardcoded constants) destination list. */
    filterDestinations(
        destinations: ExportDestination[],
        currentDestination: string | null = null
    ): ExportDestination[] {
        const hidden: ReadonlySet<string> = this.getDestinationsToFilter(currentDestination);

        if (hidden.size === 0) {
            return destinations;
        }

        return destinations.filter(
            (destination: ExportDestination): boolean => !hidden.has(destination.value)
        );
    }

    /** Drop gated rows from a `destinations.json`-driven catalog list. */
    filterCatalogDestinations(
        rows: ExportCatalogDestinationInterface[],
        currentDestination: string | null = null
    ): ExportCatalogDestinationInterface[] {
        const hidden: ReadonlySet<string> = this.getDestinationsToFilter(currentDestination);

        if (hidden.size === 0) {
            return rows;
        }

        return rows.filter(
            (row: ExportCatalogDestinationInterface): boolean => !hidden.has(row.label)
        );
    }

    /**
     * he gated destination labels this account cannot select.
     *
     * `currentDestination` is always excluded so an export already saved against a gated
     * destination keeps its value in the picker. This gates selection only — label and artwork
     * lookups must keep resolving for every destination.
     */
    private getDestinationsToFilter(currentDestination: string | null): ReadonlySet<string> {
        const account: AccountModel | null = this.appStateService.getAccount();
        const hidden: Set<string> = new Set<string>();

        for (const [label, feature] of GatedExportDestinationsService.GATED_DESTINATION_LABELS) {
            if (label !== currentDestination && !(account?.hasFeatureEnabled(feature) ?? false)) {
                hidden.add(label);
            }
        }

        return hidden;
    }
}
